import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MapPin, Navigation, Search, Star, Clock, X, Sparkles, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 59.7444, lng: 5.4887 };
const libraries = ['places', 'geometry'] as any;

const LOCATION_TYPE_ICONS = {
  'airport': '✈️',
  'barracks': '🏗️',
  'warehouse': '📦',
  'industrial': '🏭',
  'hotel': '🏨',
  'city': '🏙️',
  'other': '📍'
};

const LOCATION_TYPE_COLORS = {
  'airport': 'from-blue-500 to-cyan-500',
  'barracks': 'from-orange-500 to-amber-500',
  'warehouse': 'from-gray-500 to-slate-500',
  'industrial': 'from-purple-500 to-indigo-500',
  'hotel': 'from-pink-500 to-rose-500',
  'city': 'from-green-500 to-emerald-500',
  'other': 'from-teal-500 to-cyan-500'
};

export default function UberLocationPicker({ isOpen, onClose, onSelect, title = 'Select location' }: any) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState<any>(null);
  const autocompleteService = useRef<any>(null);
  const geocoder = useRef<any>(null);
  const [recentSearches, setRecentSearches] = useState<any[]>([]);
  const [shortcuts, setShortcuts] = useState<any[]>([]);

  useEffect(() => {
    if ((window as any).google?.maps) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
      geocoder.current = new (window as any).google.maps.Geocoder();
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('recent_locations');
    if (saved) {
      try { setRecentSearches(JSON.parse(saved)); } catch (e: any) {}
    }
    
    // Fetch location shortcuts from backend
    fetchShortcuts();
  }, []);

  const fetchShortcuts = async () => {
    try {
      const response = await axios.get(`${API}/admin/location-shortcuts`);
      setShortcuts(response.data);
    } catch (error: any) {
      console.error('Error fetching shortcuts:', error);
      // Silently fail - shortcuts are optional
    }
  };

  const handleSearch = (query: any) => {
    setSearchQuery(query);
    if (query.length < 2) { setSuggestions([]); return; }
    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { 
          input: query, 
          componentRestrictions: { country: 'no' }
          // Removed types to allow all types (geocode is default which includes everything)
        },
        (predictions: any, status: any) => {
          if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else { 
            setSuggestions([]); 
          }
        }
      );
    }
  };

  const selectLocation = (location: any) => {
    setSelectedLocation(location);
    setSearchQuery(location.name || location.description);
    setSuggestions([]);

    if (geocoder.current && location.description) {
      geocoder.current.geocode({ address: location.description }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          const pos = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
          setMarkerPosition(pos);
          setMapCenter(pos);
        }
      });
    } else if (location.lat && location.lng) {
      const pos = { lat: location.lat, lng: location.lng };
      setMarkerPosition(pos);
      setMapCenter(pos);
    }
  };

  const handleMapClick = useCallback((event: any) => {
    const pos = { lat: event.detail.latLng.lat, lng: event.detail.latLng.lng };
    setMarkerPosition(pos);
    setMapCenter(pos);
    if (geocoder.current) {
      geocoder.current.geocode({ location: pos }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          setSelectedLocation({ name: results[0].formatted_address, description: results[0].formatted_address, lat: pos.lat, lng: pos.lng });
          setSearchQuery(results[0].formatted_address);
          // toast.success(t('uberLocation.exactLocationSelected'));
        }
      });
    }
  }, []);

  const handleMarkerDragEnd = useCallback((event: any) => {
    const pos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    setMarkerPosition(pos);
    setMapCenter(pos);
    if (geocoder.current) {
      geocoder.current.geocode({ location: pos }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          setSelectedLocation({ name: results[0].formatted_address, description: results[0].formatted_address, lat: pos.lat, lng: pos.lng });
          setSearchQuery(results[0].formatted_address);
        }
      });
    }
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: any) => {
          const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
          setMarkerPosition(pos);
          setMapCenter(pos);
          if (geocoder.current) {
            geocoder.current.geocode({ location: pos }, (results: any, status: any) => {
              if (status === 'OK' && results[0]) {
                setSelectedLocation({ name: results[0].formatted_address, description: results[0].formatted_address, lat: pos.lat, lng: pos.lng });
                setSearchQuery(results[0].formatted_address);
                // toast.success(t('uberLocation.yourPositionFound'));
              }
            });
          }
        },
        () => console.error('Could not get current position')
      );
    }
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      const recent = [selectedLocation, ...recentSearches.filter((r: any) => r.description !== selectedLocation.description)].slice(0, 5);
      setRecentSearches(recent);
      localStorage.setItem('recent_locations', JSON.stringify(recent));
      onSelect(selectedLocation.description || selectedLocation.name, markerPosition);
      onClose();
    } else {
      // toast.error(t('uberLocation.selectLocation'));
    }
  };

  if (!(window as any).google?.maps) return null;

  const showingSuggestions = searchQuery.length >= 2 && suggestions.length > 0;
  const showingFavorites = !showingSuggestions && searchQuery.length < 2;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[95vh] p-0 dialog-premium overflow-hidden border-0">
        <div className="flex flex-col md:flex-row h-full">
          {/* Left Panel - Hyper-Modern Search */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="w-full md:w-[450px] flex flex-col bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 md:border-r border-border max-h-[50vh] md:max-h-full overflow-y-auto md:overflow-visible"
          >
            {/* Header */}
            <div className="p-8 pb-6">
              <motion.h2
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-3xl font-bold gradient-text mb-2"
              >
                {title}
              </motion.h2>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Search, select from favorites, or click on the map
              </p>
            </div>

            {/* Search */}
            <div className="px-8 pb-6 space-y-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative group"
              >
                <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-purple-500 z-10 group-focus-within:scale-110 transition-transform" />
                <Input
                  value={searchQuery}
                  onChange={(e: any) => handleSearch(e.target.value)}
                  placeholder={t('common.searchAddress')}
                  className="pl-14 pr-14 h-16 text-lg font-medium border-2 border-border rounded-2xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/30 transition-all"
                  autoFocus
                />
                {searchQuery && (
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => { setSearchQuery(''); setSuggestions([]); }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                )}
              </motion.div>

              {/* GPS Button - Redesigned */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mt-4"
              >
                <button
                  onClick={getCurrentLocation}
                  className="group w-full h-14 bg-background dark:bg-slate-800 hover:bg-muted/50 dark:hover:bg-slate-700 border-2 border-border dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-400 rounded-xl transition-all duration-300 flex items-center px-4 space-x-3 shadow-sm hover:shadow-md"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-500 dark:group-hover:bg-blue-500 flex items-center justify-center transition-colors duration-300">
                    <Navigation className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-base font-medium text-foreground dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                    {t('uberLocationPicker.brukMinPosisjon', 'Bruk min posisjon')}
                  </span>
                </button>
              </motion.div>
            </div>

            {/* Results */}
            <ScrollArea className="flex-1 px-8">
              <div className="space-y-4 pb-8">
                {/* Favorites */}
                {showingFavorites && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                        <Star className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">
                        Shortcuts
                      </h3>
                    </div>
                    
                    {shortcuts.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No shortcuts available
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {shortcuts.map((shortcut: any, idx: any) => {
                          const icon = (LOCATION_TYPE_ICONS as any)[shortcut.location_type] || LOCATION_TYPE_ICONS['other'];
                          const color = (LOCATION_TYPE_COLORS as any)[shortcut.location_type] || LOCATION_TYPE_COLORS['other'];
                          
                          return (
                            <motion.div
                              key={shortcut.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              whileHover={{ x: 4 }}
                            >
                              <Card
                                className="p-5 cursor-pointer hover:shadow-xl transition-all border-2 border-transparent hover:border-purple-200 dark:hover:border-purple-800 rounded-2xl backdrop-blur-sm"
                                onClick={() => selectLocation({ 
                                  name: shortcut.shortcut,
                                  description: shortcut.full_address,
                                  address: shortcut.full_address
                                })}
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-2xl shadow-lg`}>
                                    {icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-bold text-foreground dark:text-white text-base">
                                        {shortcut.shortcut}
                                      </h4>
                                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs font-medium rounded">
                                        Snarvei
                                      </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground dark:text-muted-foreground truncate">
                                      {shortcut.full_address}
                                    </p>
                                  </div>
                                  <motion.div whileHover={{ scale: 1.2 }}>
                                    <MapPin className="w-6 h-6 text-purple-500" />
                                  </motion.div>
                                </div>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Search Results */}
                {showingSuggestions && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Zap className="w-5 h-5 text-purple-500" />
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Resultater</h3>
                    </div>
                    <AnimatePresence>
                      {suggestions.map((suggestion: any, idx: any) => (
                        <motion.div
                          key={suggestion.place_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: idx * 0.03 }}
                          whileHover={{ x: 4 }}
                        >
                          <Card
                            className="p-5 cursor-pointer hover:shadow-xl transition-all border-2 border-transparent hover:border-purple-200 rounded-2xl mb-3"
                            onClick={() => selectLocation({ description: suggestion.description, placeId: suggestion.place_id })}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 flex items-center justify-center">
                                <MapPin className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-foreground dark:text-white mb-1">{suggestion.structured_formatting.main_text}</h4>
                                <p className="text-sm text-muted-foreground dark:text-muted-foreground truncate">{suggestion.structured_formatting.secondary_text}</p>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}

                {/* Recent */}
                {!showingSuggestions && !showingFavorites && recentSearches.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">{t('auto.nylig_skt', 'Recently searched')}</h3>
                    </div>
                    {recentSearches.map((loc: any, idx: any) => (
                      <Card key={idx} className="p-5 cursor-pointer hover:shadow-xl transition-all rounded-2xl mb-3" onClick={() => selectLocation(loc)}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-blue-600" />
                          </div>
                          <p className="font-medium text-foreground dark:text-white flex-1 truncate">{loc.name || loc.description}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </motion.div>

          {/* Right Panel - Map */}
          <div className="flex-1 relative bg-muted">
            <Map
              style={mapContainerStyle}
              defaultZoom={11}
              defaultCenter={mapCenter}
              center={mapCenter}
              zoom={markerPosition ? 15 : 11}
              onClick={handleMapClick}
              disableDefaultUI={true}
              zoomControl={true}
              mapId={process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID'}
            >
              {markerPosition && (
                <AdvancedMarker
                  position={markerPosition}
                  draggable={true}
                  onDragEnd={handleMarkerDragEnd}
                  title={t('manage.dragForPrecisePlacement', 'Drag me for exact placement')}
                />
              )}
            </Map>

            {/* Floating Confirm Card - COMPLETELY FIXED */}
            {selectedLocation && (
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="absolute bottom-6 left-6 right-6"
              >
                <Card className="glass-card shadow-2xl border-2 border-white/50/50 overflow-hidden">
                  {/* Compact Single Row Layout */}
                  <div className="p-5 flex items-center gap-4">
                    {/* Icon */}
                    <motion.div
                      className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl flex-shrink-0"
                      animate={{ scale: [1, 1.08, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <MapPin className="w-8 h-8 text-white" />
                    </motion.div>
                    
                    {/* Location Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-lg text-foreground dark:text-white mb-1 truncate">
                        {selectedLocation.name || 'Valgt lokasjon'}
                      </h4>
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground truncate">
                        {selectedLocation.description || selectedLocation.address}
                      </p>
                    </div>
                    
                    {/* Confirm Button - Compact */}
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="flex-shrink-0">
                      <Button
                        onClick={handleConfirm}
                        className="h-16 px-8 btn-premium text-base font-bold shadow-xl rounded-xl whitespace-nowrap"
                      >
                        <MapPin className="w-5 h-5 mr-2" />
                        Confirm
                      </Button>
                    </motion.div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Map Instruction */}
            {!markerPosition && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-8 left-1/2 transform -translate-x-1/2"
              >
                <Card className="glass-card px-6 py-3 shadow-2xl border-2 border-purple-200 dark:border-purple-800">
                  <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                      <MapPin className="w-5 h-5 text-purple-500" />
                    </motion.div>
                    {t('uberLocationPicker.clickOrDragPinFor', 'Click or drag pin for exact location')}
                  </p>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
